import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, InlineError, Input, Skeleton, Textarea } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { ProductionPoDocument } from './ProductionPoDocument';
import { referenceName } from './PoDocumentView';
import { buildProductionPo } from './productionPo';
import { sendPoProblem } from './financialGate';
import { cgErrorMessage } from './types';
import { fetchSupplierLetterhead } from './queries';
import { FinancialGatePanel } from './FinancialGatePanel';
import { ProofImage } from './ProofImage';
import { ArtworkDownload } from './ArtworkDownload';
import {
  cgKeys,
  useClientApproved,
  useGateState,
  useOrderApproval,
  useOrderClientLogo,
  useOrderReferenceFiles,
  useOrderSupplier,
  useSendPoToSupplier,
  useUpdateCgOrder,
} from './hooks';
import type { CgOrder, CgOrderItem } from './types';

/**
 * Reviewing the PO before it goes, as a document rather than a form.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Kyu: "What does sending a PO to ES look like? Should there be a section in
 * the order profile just for it? opens a pop up dialog box so we can really
 * see each field, detail per detail before we send it?"
 *
 * The drawer is a database form — fields in the order the columns were added,
 * none of it grouped by what the factory reads. So somebody filled a form,
 * pressed Send, and found out afterwards what the PO actually said. This is
 * the standard ERP shape instead: the PO exists as a reviewable draft, and
 * issuing it is a distinct act taken while LOOKING AT IT.
 *
 * ── Editable, on the second look ───────────────────────────────────────────
 *
 * Kyu first chose a read-only preview, then used it: "cannot edit PO. the prob
 * is it doesnt have the dialog box I need to fill out the info and shows the
 * approved assets attached." Which is the round-trip the read-only version
 * always implied — spot a missing ship-to, close, open Quick edit, find the
 * field among thirty others, come back.
 *
 * So the fields the SHEET prints are editable here, in the order they print,
 * and nothing else is. Quantities, prices and product names stay in the drawer
 * because they are the order rather than the paperwork, and money must never
 * be one careless keystroke from a document that carries none.
 *
 * ── The preview and the frozen sheet come from the same rules ──────────────
 *
 * buildProductionPo here; issue_po_revision in the database when Send is
 * pressed. Both read the same order row, the same items and the same supplier
 * letterhead, so what was reviewed is what is issued. The rendered component
 * is literally the same one the frozen revision renders through afterwards.
 */
export function SendPoDialog({
  order,
  items,
  onClose,
}: {
  order: CgOrder;
  items: CgOrderItem[];
  onClose: () => void;
}) {
  const { client, access } = useAuth();
  const supplier = useOrderSupplier(order.id);
  const approved = useClientApproved(order.id);
  const gate = useGateState(order.id);
  const send = useSendPoToSupplier();
  const update = useUpdateCgOrder();
  const assets = useOrderApproval(order.id);
  const [sendError, setSendError] = useState('');

  // Local until saved. Typing straight into a mutation would fire a write per
  // keystroke; a draft the person saves is also the thing that makes "what am
  // I about to send" answerable — the preview below re-renders from it.
  const [shipTo, setShipTo] = useState(order.ship_to);
  const [targetDate, setTargetDate] = useState(order.target_ship_date ?? '');
  const [notes, setNotes] = useState(order.notes_to_manufacturer);
  // Per line, the two fields the sheet prints and the drawer buries among
  // price and cost. Keyed by item id so a re-render cannot reorder them onto
  // the wrong line.
  const [lineEdits, setLineEdits] = useState<Record<string, { sku: string; spec: string }>>(() =>
    Object.fromEntries(
      items.map((item) => [item.id, { sku: item.sku ?? '', spec: item.spec ?? '' }]),
    ),
  );
  const editOf = (item: CgOrderItem) =>
    lineEdits[item.id] ?? { sku: item.sku ?? '', spec: item.spec ?? '' };
  const linesDirty = items.some(
    (item) => editOf(item).sku !== (item.sku ?? '') || editOf(item).spec !== (item.spec ?? ''),
  );
  const dirty =
    shipTo !== order.ship_to ||
    targetDate !== (order.target_ship_date ?? '') ||
    notes !== order.notes_to_manufacturer ||
    linesDirty;

  // What the client signed off, as it will be attached. Newest approved
  // version per component, the same rule the frozen snapshot uses.
  const proofs = (assets.data?.assets ?? []).filter(
    (asset) => asset.state === 'approved' || asset.state === 'final_approved',
  );
  // The fifth file. Kyu: "just warn not block" when it is missing.
  const logo = useOrderClientLogo(order.id);
  const refs = useOrderReferenceFiles(order.id);

  const organizationId = supplier.data?.organizationId ?? null;
  const letterhead = useQuery({
    queryKey: [...cgKeys.all, 'letterhead', organizationId ?? ''],
    queryFn: () => fetchSupplierLetterhead(client, organizationId ?? ''),
    enabled: access.status === 'approved' && organizationId !== null,
    retry: false,
  });

  const loading = supplier.isPending || approved.isPending || gate.isPending;

  // The preview shows the DRAFT, not the saved row, so an edit is visible
  // before it is committed. Send is disabled while unsaved, so what is issued
  // is always what was on screen.
  const draft = {
    ...order,
    ship_to: shipTo,
    target_ship_date: targetDate === '' ? null : targetDate,
    notes_to_manufacturer: notes,
  };
  const draftItems = items.map((item) => ({
    ...item,
    sku: editOf(item).sku,
    spec: editOf(item).spec,
  }));

  // The same rule the drawer's button uses — one sentence naming the first
  // thing actually stopping the send, in fastest-to-fix order.
  const supplierLinked = supplier.isPending ? undefined : supplier.data !== null;
  const problem = sendPoProblem(
    order.status,
    gate.data ?? [],
    supplierLinked,
    approved.isPending ? undefined : approved.data,
  );

  // Nothing here blocks — the document is legal to send without them — but
  // each is the kind of omission a factory phones about. Said BEFORE the send,
  // while fixing it is an edit rather than a revision.
  const warnings: string[] = [];
  if (draft.target_ship_date === null) {
    warnings.push('No target ship date. The sheet will print "Not yet committed".');
  }
  if (draft.notes_to_manufacturer.trim() === '') {
    warnings.push('No notes to manufacturer — no build instructions will print.');
  }
  if (draft.ship_to.trim() === '') {
    warnings.push('No ship-to address on this order.');
  }
  if (!logo.isPending && (logo.data ?? null) === null) {
    // A warning, not a block. Kyu, 2 Sep: "just warn not block." A forgotten
    // logo should not turn into a stuck order; it should turn into a sentence
    // somebody reads before pressing Send.
    warnings.push(
      'No client logo on this order. The factory will get the artwork without it — add it under Client logo on the order page first if they need it.',
    );
  }
  const skuless = items.filter((item) => editOf(item).sku.trim() === '');
  if (skuless.length > 0) {
    warnings.push(
      skuless.length === 1
        ? '1 line item has no SKU.'
        : `${String(skuless.length)} line items have no SKU.`,
    );
  }

  return (
    <Dialog open title={`Review & send PO — ${order.order_no}`} wide onClose={onClose}>
      {loading ? (
        <Skeleton lines={6} label="Preparing the PO" />
      ) : (
        <>
          {problem === null ? null : <InlineError>{problem}</InlineError>}
          {sendError === '' ? null : <InlineError>{sendError}</InlineError>}
          {warnings.length === 0 ? null : (
            <ul className="send-po-warnings">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}

          {/* The fields the sheet prints, in the order it prints them, and
              nothing else. Everything here re-renders the preview below. */}
          <div className="send-po-fields">
            <FinancialGatePanel order={order} onError={setSendError} />

            <Input
              label="Target ship date"
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
              hint="The date the factory is asked to hit. Printed on the sheet."
            />
            <Textarea
              label="Ship to"
              rows={3}
              value={shipTo}
              placeholder="Name, street, city, country, postcode, phone"
              onChange={(event) => setShipTo(event.target.value)}
              hint="Printed line for line, exactly as typed."
            />
            {items.length === 0 ? null : (
              <fieldset className="send-po-lines">
                <legend>What to make</legend>
                {/* Quantity, product and price are NOT here. They are the
                    order rather than the paperwork, and money must never be
                    one careless keystroke from a document that carries none.
                    SKU and spec are what the sheet prints and what a factory
                    quotes back. */}
                {items.map((item) => (
                  <div key={item.id} className="send-po-line">
                    <span className="send-po-line-name">
                      {item.units} ×{' '}
                      {item.description.trim() === '' ? item.product_type : item.description}
                    </span>
                    <Input
                      label={`SKU for ${item.product_type}`}
                      labelHidden
                      placeholder="SKU"
                      value={editOf(item).sku}
                      onChange={(event) =>
                        setLineEdits((current) => ({
                          ...current,
                          [item.id]: { ...editOf(item), sku: event.target.value },
                        }))
                      }
                    />
                    <Input
                      label={`Spec for ${item.product_type}`}
                      labelHidden
                      placeholder="Spec"
                      value={editOf(item).spec}
                      onChange={(event) =>
                        setLineEdits((current) => ({
                          ...current,
                          [item.id]: { ...editOf(item), spec: event.target.value },
                        }))
                      }
                    />
                  </div>
                ))}
              </fieldset>
            )}

            <Textarea
              label="Notes to manufacturer"
              rows={3}
              value={notes}
              placeholder="Build instructions the factory reads."
              onChange={(event) => setNotes(event.target.value)}
              hint="The only free text on the sheet. Internal notes stay internal."
            />
            <div className="send-po-save">
              <Button
                variant="secondary"
                size="sm"
                disabled={!dirty || update.isPending}
                onClick={() => {
                  setSendError('');
                  update.mutate(
                    {
                      orderId: order.id,
                      update: {
                        shipTo,
                        targetShipDate: targetDate === '' ? null : targetDate,
                        notesToManufacturer: notes,
                      },
                      // Only when a line actually changed. replaceCgItems
                      // deletes and re-inserts every row, so sending them
                      // unchanged would churn the whole set on each save.
                      ...(linesDirty
                        ? {
                            items: items.map((item) => ({
                              productType: item.product_type,
                              description: item.description,
                              units: item.units,
                              unitPriceUsd: Number(item.unit_price_usd),
                              unitCostUsd:
                                item.unit_cost_usd === null ? null : Number(item.unit_cost_usd),
                              sku: editOf(item).sku,
                              spec: editOf(item).spec,
                            })),
                          }
                        : {}),
                    },
                    { onError: (cause) => setSendError(cgErrorMessage(cause.message)) },
                  );
                }}
              >
                {update.isPending ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </Button>
              {!dirty ? null : (
                <span className="order-page-muted">
                  Save before sending — the sheet is frozen from the saved order.
                </span>
              )}
            </div>
          </div>

          <ProductionPoDocument
            po={buildProductionPo(draft, draftItems, {
              manufacturer: supplier.data?.organizationName ?? '',
              attn: letterhead.data?.attn ?? '',
              address: letterhead.data?.address ?? '',
              issuedOn: new Date().toISOString().slice(0, 10),
            })}
          >
            {/* Attached to the sheet, because "build to these" is half of what
                a PO says. issue_po_revision freezes the same set into the
                snapshot, so what is reviewed here is what the factory opens. */}
            {proofs.length === 0 ? null : (
              <section className="po-section">
                <h2 className="po-section-label">APPROVED ARTWORK</h2>
                <ul className="po-doc-proofs">
                  {proofs.map((proof) => (
                    <li key={proof.id}>
                      <ProofImage url={proof.url} alt={proof.component_type} />
                      <span>
                        {proof.component_type} v{proof.version}
                      </span>
                      <ArtworkDownload url={proof.url} component={proof.component_type} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {/* Every reference file, previewed exactly as issue_po_revision will
                freeze it — the logo and both optional slots. Ced, 4 September:
                the packaging file for Lazzara went to Jen on WhatsApp because
                there was nowhere here to put it. This is where he checks it
                actually made it onto the sheet. */}
            {(refs.data ?? []).length === 0 ? null : (
              <section className="po-section">
                <h2 className="po-section-label">REFERENCE FILES</h2>
                <ul className="po-doc-proofs">
                  {(refs.data ?? []).map((file) => (
                    <li key={file.kind}>
                      <ProofImage url={file.url} alt={referenceName(file)} />
                      <span>
                        {referenceName(file)} v{file.version}
                      </span>
                      <ArtworkDownload url={file.url} component={referenceName(file)} />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </ProductionPoDocument>

          <div className="send-po-actions">
            <Button variant="secondary" onClick={onClose} disabled={send.isPending}>
              Close
            </Button>
            <Button
              disabled={problem !== null || dirty || send.isPending}
              onClick={() =>
                send.mutate(order.id, {
                  onSuccess: onClose,
                  // The dialog stays open on failure: the document being
                  // looked at is the context for understanding the refusal.
                  onError: (cause) => setSendError(cgErrorMessage(cause.message)),
                })
              }
            >
              {send.isPending ? 'Sending…' : 'Send this PO to the supplier'}
            </Button>
          </div>
          <p className="send-po-note">
            Sending freezes this sheet as Revision A, stamps the PO date and moves the order to PO
            Sent to ES. Later changes issue a new revision — this one is kept.
          </p>
        </>
      )}
    </Dialog>
  );
}
